import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { Layout } from '../../components/Layout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from '../../components/ui/dialog';
import { toast } from 'sonner';
import { MagnifyingGlass, Plus, UserPlus, PencilSimple, ArrowClockwise, Wallet, WhatsappLogo, Link as LinkIcon, PaperPlaneTilt } from '@phosphor-icons/react';
import { GeneratePaymentLinkDialog } from '../../components/admin/GeneratePaymentLinkDialog';

const fmtINR = (n) => '₹ ' + Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 });

const STATUS_OPTIONS = [
  { k: 'active', label: 'Active' },
  { k: 'lead', label: 'Lead' },
  { k: 'inactive', label: 'Inactive' },
  { k: 'lost', label: 'Lost' },
];

const emptyForm = {
  mobile: '', name: '', alternate_mobile: '', address: '', site_location: '',
  assigned_executive: '', status: 'active', remarks: '',
};

function CustomerMaster() {
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [executives, setExecutives] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);   // customer being edited (or null for new)
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [assignFor, setAssignFor] = useState(null);   // customer for assign dialog
  const [assignForm, setAssignForm] = useState({ executive_user_id: '', remarks: '' });
  const [walletFor, setWalletFor] = useState(null);        // customer for wallet dialog
  const [walletData, setWalletData] = useState(null);       // { available, total_advance, ... }
  const [walletLoading, setWalletLoading] = useState(false);
  const [advanceForm, setAdvanceForm] = useState({ amount: '', payment_date: new Date().toISOString().slice(0, 10), mode: 'Cash', reference: '', remarks: '' });
  const [waFor, setWaFor] = useState(null);                 // customer for WA quick send dialog
  const [waForm, setWaForm] = useState({ body_override: '', event_key: 'customer.welcome' });
  const [waTemplates, setWaTemplates] = useState([]);
  const [payLinkFor, setPayLinkFor] = useState(null);       // customer for payment-link dialog

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/api/admin/customers', {
        params: { search: search || undefined, limit: 500 },
      });
      setCustomers(data.customers || []);
      setTotal(data.total || 0);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load');
    } finally { setLoading(false); }
  }, [search]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get('/api/users/executives').then(r => setExecutives(r.data.executives || [])).catch(() => {});
    api.get('/api/admin/notifications/templates').then(r => setWaTemplates(r.data || [])).catch(() => {});
  }, []);

  const openNew = () => { setForm(emptyForm); setEditing(null); setShowForm(true); };
  const openEdit = (c) => {
    setForm({ ...emptyForm, ...c });
    setEditing(c);
    setShowForm(true);
  };

  const save = async () => {
    if (!form.mobile || form.mobile.replace(/\D/g, '').length < 10) {
      toast.error('Valid 10-digit mobile required'); return;
    }
    if (!form.name?.trim()) { toast.error('Customer name required'); return; }
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/api/admin/customers/${encodeURIComponent(editing.mobile)}`, {
          name: form.name, address: form.address, alternate_mobile: form.alternate_mobile,
          site_location: form.site_location, status: form.status, remarks: form.remarks,
        });
        toast.success('Customer updated');
      } else {
        await api.post('/api/admin/customers', form);
        toast.success('Customer created');
      }
      setShowForm(false);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const openAssign = (c) => {
    setAssignFor(c);
    setAssignForm({ executive_user_id: c.assigned_executive || '', remarks: '' });
  };

  const assign = async () => {
    if (!assignForm.executive_user_id) { toast.error('Please pick an executive'); return; }
    try {
      const { data } = await api.post(
        `/api/admin/customers/${encodeURIComponent(assignFor.mobile)}/assign`,
        assignForm,
      );
      toast.success(`Assigned to ${data.executive_name}`);
      setAssignFor(null);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Assignment failed');
    }
  };

  const openWallet = async (c) => {
    setWalletFor(c);
    setWalletData(null);
    setAdvanceForm({ amount: '', payment_date: new Date().toISOString().slice(0, 10), mode: 'Cash', reference: '', remarks: '' });
    setWalletLoading(true);
    try {
      const { data } = await api.get(`/api/admin/customers/${encodeURIComponent(c.mobile)}/wallet`);
      setWalletData(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load wallet');
    } finally { setWalletLoading(false); }
  };

  const recordAdvance = async () => {
    const amt = parseFloat(advanceForm.amount);
    if (!amt || amt <= 0) { toast.error('Enter a positive amount'); return; }
    if (!advanceForm.payment_date) { toast.error('Payment date required'); return; }
    try {
      await api.post(
        `/api/admin/customers/${encodeURIComponent(walletFor.mobile)}/advances`,
        { ...advanceForm, amount: amt },
      );
      toast.success('Advance recorded');
      // Refresh wallet + list
      const { data } = await api.get(`/api/admin/customers/${encodeURIComponent(walletFor.mobile)}/wallet`);
      setWalletData(data);
      setAdvanceForm({ amount: '', payment_date: new Date().toISOString().slice(0, 10), mode: 'Cash', reference: '', remarks: '' });
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    }
  };

  const openWa = (c) => {
    setWaFor(c);
    setWaForm({ body_override: '', event_key: waTemplates[0]?.event_key || 'customer.welcome' });
  };

  const sendWa = async () => {
    if (!waFor?.mobile) return;
    try {
      await api.post('/api/admin/notifications/send', {
        event_key: waForm.event_key,
        to: waFor.mobile,
        body_override: waForm.body_override || undefined,
        send_now: true,
        ref_id: waFor.mobile,
        ref_type: 'customer',
      });
      toast.success('WhatsApp queued');
      setWaFor(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Send failed');
    }
  };

  const statusColor = (s) => {
    switch (s) {
      case 'active': return 'bg-green-100 text-green-800 hover:bg-green-100';
      case 'lead': return 'bg-blue-100 text-blue-800 hover:bg-blue-100';
      case 'inactive': return 'bg-zinc-200 text-zinc-700 hover:bg-zinc-200';
      case 'lost': return 'bg-red-100 text-red-800 hover:bg-red-100';
      default: return 'bg-zinc-100 text-zinc-700 hover:bg-zinc-100';
    }
  };

  return (
    <Layout>
      <div className="space-y-6" data-testid="customer-master-page">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="text-3xl font-light tracking-tight text-zinc-950">Customer Master</h1>
            <p className="text-sm text-zinc-500 mt-1">{total} customer{total === 1 ? '' : 's'} · Assign executives, track leads, avoid duplicates.</p>
          </div>
          <div className="flex gap-2">
            <div className="relative">
              <MagnifyingGlass className="absolute left-2 top-2.5 text-zinc-400" size={16} />
              <Input value={search} onChange={(e) => setSearch(e.target.value)}
                     placeholder="Search name or mobile"
                     className="pl-8 w-64" data-testid="search-input" />
            </div>
            <Button variant="outline" onClick={load} data-testid="reload-btn"><ArrowClockwise size={16} /></Button>
            <Button onClick={openNew} className="bg-orange-500 hover:bg-orange-600 text-white" data-testid="new-customer-btn">
              <Plus size={16} className="mr-1" /> New Customer
            </Button>
          </div>
        </div>

        <Card className="border-zinc-300 rounded-sm"><CardContent className="p-0 overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-zinc-100 text-zinc-600 uppercase tracking-wider text-[10px]">
              <tr>
                <th className="p-3 text-left">Mobile</th>
                <th className="p-3 text-left">Name</th>
                <th className="p-3 text-left">Site Location</th>
                <th className="p-3 text-left">Alt Mobile</th>
                <th className="p-3 text-left">Executive</th>
                <th className="p-3 text-left">Status</th>
                <th className="p-3 text-right">JO Count</th>
                <th className="p-3 text-right">Outstanding</th>
                <th className="p-3 text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={9} className="p-6 text-center text-zinc-400">Loading…</td></tr>
              ) : customers.length === 0 ? (
                <tr><td colSpan={9} className="p-6 text-center text-zinc-400">No customers</td></tr>
              ) : customers.map((c) => (
                <tr key={c.mobile} className="border-b border-zinc-100 hover:bg-zinc-50" data-testid={`customer-row-${c.mobile}`}>
                  <td className="p-3 font-mono text-zinc-800">{c.mobile}</td>
                  <td className="p-3">
                    <button className="text-blue-700 hover:underline font-medium text-left" data-testid={`open-customer-${c.mobile}`}
                      onClick={() => navigate(`/customers/${c.mobile}`)}>{c.name}</button>
                  </td>
                  <td className="p-3 text-zinc-700">{c.site_location || '—'}</td>
                  <td className="p-3 font-mono text-zinc-600">{c.alternate_mobile || '—'}</td>
                  <td className="p-3 text-zinc-700">{c.executive_name || '—'}</td>
                  <td className="p-3"><Badge className={statusColor(c.status || 'active') + ' text-[10px]'}>{(c.status || 'active').toUpperCase()}</Badge></td>
                  <td className="p-3 text-right text-zinc-800">{c.jo_count || 0}</td>
                  <td className="p-3 text-right text-red-700 font-bold">{fmtINR(c.outstanding)}</td>
                  <td className="p-3">
                    <div className="flex gap-1 justify-center flex-wrap">
                      <Button size="sm" variant="outline" onClick={() => openEdit(c)} data-testid={`edit-${c.mobile}`} title="Edit">
                        <PencilSimple size={12} />
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => openAssign(c)} data-testid={`assign-${c.mobile}`} title="Assign to executive">
                        <UserPlus size={12} />
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => openWallet(c)} data-testid={`wallet-${c.mobile}`} title="Advance Wallet" className="text-emerald-700">
                        <Wallet size={12} />
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => openWa(c)} data-testid={`wa-${c.mobile}`} title="Send WhatsApp" className="text-green-700">
                        <WhatsappLogo size={12} />
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => setPayLinkFor(c)} data-testid={`paylink-${c.mobile}`} title="Send Payment Link" className="text-blue-700">
                        <LinkIcon size={12} />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent></Card>
      </div>

      {/* Create / Edit dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit Customer' : 'New Customer'}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-wider text-zinc-500">Mobile Number*</Label>
              <Input value={form.mobile} onChange={(e) => setForm((f) => ({ ...f, mobile: e.target.value }))}
                     disabled={!!editing} data-testid="form-mobile" placeholder="10-digit mobile" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-zinc-500">Customer Name*</Label>
              <Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} data-testid="form-name" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-zinc-500">Alternate Mobile</Label>
              <Input value={form.alternate_mobile} onChange={(e) => setForm((f) => ({ ...f, alternate_mobile: e.target.value }))} data-testid="form-alt" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-zinc-500">Site Location</Label>
              <Input value={form.site_location} onChange={(e) => setForm((f) => ({ ...f, site_location: e.target.value }))} data-testid="form-site" />
            </div>
            <div className="md:col-span-2">
              <Label className="text-xs uppercase tracking-wider text-zinc-500">Address</Label>
              <Textarea rows={2} value={form.address} onChange={(e) => setForm((f) => ({ ...f, address: e.target.value }))} data-testid="form-address" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-zinc-500">Status</Label>
              <Select value={form.status || 'active'} onValueChange={(v) => setForm((f) => ({ ...f, status: v }))}>
                <SelectTrigger data-testid="form-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {STATUS_OPTIONS.map((s) => <SelectItem key={s.k} value={s.k}>{s.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {!editing && (
              <div>
                <Label className="text-xs uppercase tracking-wider text-zinc-500">Assign to Executive</Label>
                <Select value={form.assigned_executive || 'none'} onValueChange={(v) => setForm((f) => ({ ...f, assigned_executive: v === 'none' ? '' : v }))}>
                  <SelectTrigger data-testid="form-assigned"><SelectValue placeholder="Unassigned" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Unassigned</SelectItem>
                    {executives.map((e) => <SelectItem key={e.user_id} value={e.user_id}>{e.name || e.user_id} ({e.role})</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="md:col-span-2">
              <Label className="text-xs uppercase tracking-wider text-zinc-500">Remarks</Label>
              <Textarea rows={2} value={form.remarks} onChange={(e) => setForm((f) => ({ ...f, remarks: e.target.value }))} data-testid="form-remarks" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving} className="bg-orange-500 hover:bg-orange-600 text-white" data-testid="save-customer-btn">
              {saving ? 'Saving…' : (editing ? 'Save Changes' : 'Create Customer')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assign dialog */}
      <Dialog open={!!assignFor} onOpenChange={(o) => !o && setAssignFor(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Assign to Executive</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="text-sm text-zinc-600">
              Customer: <span className="font-bold text-zinc-950">{assignFor?.name}</span> ({assignFor?.mobile})
              {assignFor?.executive_name && (
                <div className="text-xs text-zinc-500 mt-1">Currently assigned to: {assignFor.executive_name}</div>
              )}
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-zinc-500">Executive*</Label>
              <Select value={assignForm.executive_user_id || 'none'} onValueChange={(v) => setAssignForm((f) => ({ ...f, executive_user_id: v === 'none' ? '' : v }))}>
                <SelectTrigger data-testid="assign-exec-select"><SelectValue placeholder="Pick an executive" /></SelectTrigger>
                <SelectContent>
                  {executives.map((e) => <SelectItem key={e.user_id} value={e.user_id}>{e.name || e.user_id} ({e.role})</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-zinc-500">Note (visible in notification)</Label>
              <Textarea rows={2} value={assignForm.remarks} onChange={(e) => setAssignForm((f) => ({ ...f, remarks: e.target.value }))} data-testid="assign-remarks" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAssignFor(null)}>Cancel</Button>
            <Button onClick={assign} className="bg-orange-500 hover:bg-orange-600 text-white" data-testid="assign-submit-btn">
              Assign & Notify
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Wallet dialog (Phase 3 — Advance) */}
      <Dialog open={!!walletFor} onOpenChange={(o) => !o && setWalletFor(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Advance Wallet — {walletFor?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="text-xs text-zinc-500">Mobile: <span className="font-mono">{walletFor?.mobile}</span></div>
            {walletLoading ? (
              <div className="p-6 text-center text-zinc-400 text-sm">Loading wallet…</div>
            ) : walletData ? (
              <div className="grid grid-cols-3 gap-3">
                <div className="p-3 rounded-sm bg-emerald-50 border border-emerald-200">
                  <div className="text-[10px] uppercase tracking-wider text-emerald-700">Available</div>
                  <div className="text-2xl font-bold text-emerald-800 mt-1" data-testid="wallet-available">{fmtINR(walletData.available)}</div>
                </div>
                <div className="p-3 rounded-sm bg-blue-50 border border-blue-200">
                  <div className="text-[10px] uppercase tracking-wider text-blue-700">Total Received</div>
                  <div className="text-lg font-bold text-blue-800 mt-1">{fmtINR(walletData.total_advance)}</div>
                </div>
                <div className="p-3 rounded-sm bg-zinc-50 border border-zinc-200">
                  <div className="text-[10px] uppercase tracking-wider text-zinc-500">Applied</div>
                  <div className="text-lg font-bold text-zinc-700 mt-1">{fmtINR(walletData.total_applied)}</div>
                </div>
              </div>
            ) : null}

            <div className="border-t border-zinc-200 pt-4">
              <div className="text-xs uppercase tracking-wider text-zinc-500 mb-2">Record New Advance</div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs uppercase tracking-wider text-zinc-500">Amount*</Label>
                  <Input type="number" value={advanceForm.amount} onChange={(e) => setAdvanceForm((f) => ({ ...f, amount: e.target.value }))} data-testid="advance-amount" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-zinc-500">Payment Date*</Label>
                  <Input type="date" value={advanceForm.payment_date} onChange={(e) => setAdvanceForm((f) => ({ ...f, payment_date: e.target.value }))} data-testid="advance-date" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-zinc-500">Mode</Label>
                  <Select value={advanceForm.mode} onValueChange={(v) => setAdvanceForm((f) => ({ ...f, mode: v }))}>
                    <SelectTrigger data-testid="advance-mode"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {['Cash', 'UPI', 'Bank Transfer', 'Cheque', 'Card', 'Other'].map((m) => (
                        <SelectItem key={m} value={m}>{m}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-zinc-500">Reference</Label>
                  <Input value={advanceForm.reference} onChange={(e) => setAdvanceForm((f) => ({ ...f, reference: e.target.value }))} data-testid="advance-ref" placeholder="txn / cheque no." />
                </div>
                <div className="col-span-2">
                  <Label className="text-xs uppercase tracking-wider text-zinc-500">Remarks</Label>
                  <Textarea rows={2} value={advanceForm.remarks} onChange={(e) => setAdvanceForm((f) => ({ ...f, remarks: e.target.value }))} data-testid="advance-remarks" />
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setWalletFor(null)}>Close</Button>
            <Button onClick={recordAdvance} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="advance-save">
              <Plus size={14} className="mr-1" /> Record Advance
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Send WhatsApp dialog */}
      <Dialog open={!!waFor} onOpenChange={(o) => !o && setWaFor(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Send WhatsApp — {waFor?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="text-xs text-zinc-500">To: <span className="font-mono">{waFor?.mobile}</span></div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-zinc-500">Template Event</Label>
              <Select value={waForm.event_key} onValueChange={(v) => setWaForm((f) => ({ ...f, event_key: v }))}>
                <SelectTrigger data-testid="wa-event"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {waTemplates.length === 0 ? <SelectItem value="customer.welcome">customer.welcome</SelectItem> : waTemplates.map((t) => (
                    <SelectItem key={t.event_key} value={t.event_key}>{t.event_key}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-zinc-500">Body Override (Optional)</Label>
              <Textarea rows={4} value={waForm.body_override} onChange={(e) => setWaForm((f) => ({ ...f, body_override: e.target.value }))} data-testid="wa-body" placeholder={waTemplates.find(t => t.event_key === waForm.event_key)?.body || 'Leave blank to use template'} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setWaFor(null)}>Cancel</Button>
            <Button onClick={sendWa} className="bg-green-600 hover:bg-green-700 text-white" data-testid="wa-send">
              <PaperPlaneTilt size={14} className="mr-1" /> Send Now
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Payment Link dialog (reuse shared component) */}
      <GeneratePaymentLinkDialog
        open={!!payLinkFor}
        onOpenChange={(o) => !o && setPayLinkFor(null)}
        prefill={payLinkFor ? {
          customer_name: payLinkFor.name,
          customer_mobile: payLinkFor.mobile,
          receipt_type: 'advance',
        } : {}}
        onCreated={() => setPayLinkFor(null)}
      />
    </Layout>
  );
}

export default CustomerMaster;
export { CustomerMaster };
